// LIGHT COPY of components/topology/TopologyAssets.jsx for /event-trace-light. The dark original is untouched;
// changes to it are NOT carried here automatically.
import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, { Controls, Background, BackgroundVariant } from 'reactflow';
import 'reactflow/dist/style.css';
import { KNOWN_DEVICES, EDGE_AGENT_NAME, PROTOCOL_COLORS, ROLE_COLORS, SEVERITY_COLORS, SITE_NATIVE_PREFIX } from '../../constants/topologyLight';
import { deviceTypeLabel, deviceRoleClass, knownDeviceName } from '../../constants/deviceTypes';
import { TOPOLOGY_NODE_TYPES } from './TopologyNodesLight';
import { edgeNodeDisplayName } from '../../utils/edgeNodeDisplayName';

const FILTER_OPTIONS = ['All', 'Critical', 'High', 'Blocked'];

// Build ReactFlow graph from topology/assets + topology/edges data
function buildAssetGraph(assets, edges, edgeNode, selectedNodeId) {
  const nodes = [];
  const rfEdges = [];

  const nativePrefix = edgeNode ? (SITE_NATIVE_PREFIX[edgeNode.edge_node_id] || null) : null;
  const deviceAssets = assets.length > 0 ? assets
    : Object.entries(KNOWN_DEVICES)
        .filter(([ip]) => !nativePrefix || ip.startsWith(nativePrefix))
        .map(([ip, kd]) => ({
          ip_address: ip, asset_name: knownDeviceName(ip, kd.name), asset_type: kd.role, vendor: kd.vendor,
          risk_level: null, event_count_24h: 0, enforcement_state: null, protocols_used: [], policy_hits: [],
        }));

  const COLS = Math.min(Math.max(deviceAssets.length, 1), 5);
  const COL_GAP = 140;
  const ROW_GAP = 180;

  // Cloud node
  nodes.push({
    id: 'cloud',
    type: 'cloud',
    position: { x: 300, y: 0 },
    data: { label: 'EdgeNotary Cloud' },
    draggable: false,
  });

  // Edge agent node
  const enf = edgeNode?.enforcement_mode || 'dry-run';
  nodes.push({
    id: 'edge-agent',
    type: 'edgeAgent',
    position: { x: 270, y: 110 },
    data: {
      hostname: edgeNode ? edgeNodeDisplayName(edgeNode, EDGE_AGENT_NAME) : EDGE_AGENT_NAME,
      policiesLoaded: edgeNode?.policies_loaded || 0,
      enforcementMode: enf,
      status: edgeNode?.status || 'Offline',
    },
    draggable: true,
  });

  rfEdges.push({
    id: 'cloud-to-agent',
    source: 'cloud',
    target: 'edge-agent',
    animated: true,
    style: { stroke: '#38BDF8', strokeWidth: 1.5, strokeDasharray: '6 3' },
    label: 'mgmt tunnel',
    labelStyle: { fill: '#475569', fontSize: 10 },
    labelBgStyle: { fill: 'transparent' },
  });

  // Device nodes — dynamic row layout (up to COLS wide)
  deviceAssets.forEach((asset, idx) => {
    const ip = asset.ip_address;
    const kd = KNOWN_DEVICES[ip] || {};
    const row = Math.floor(idx / COLS);
    const col = idx % COLS;
    const rowCount = Math.min(deviceAssets.length - row * COLS, COLS);
    const rowStartX = Math.max(0, 300 - ((rowCount - 1) * COL_GAP) / 2);
    const x = rowStartX + col * COL_GAP;
    const y = 240 + row * ROW_GAP;
    const id = `node-${ip}`;
    const name = asset.asset_name || knownDeviceName(ip, kd.name) || ip;
    const vendor = asset.vendor || kd.vendor || '';
    const storedRole = kd.role || asset.asset_type || 'Device';

    nodes.push({
      id,
      type: 'otDevice',
      position: { x, y },
      data: {
        ip,
        name,
        role: deviceTypeLabel(name, vendor, storedRole),
        roleClass: deviceRoleClass(name, vendor, storedRole),
        vendor,
        riskLevel: asset.risk_level || 'normal',
        protocols: asset.protocols_used || [],
        policyCount: (asset.policy_hits || []).length,
        isSelected: selectedNodeId === id,
      },
    });

    rfEdges.push({
      id: `agent-to-${ip}`,
      source: 'edge-agent',
      target: id,
      animated: (asset.event_count_24h || 0) > 0,
      style: {
        stroke: asset.risk_level === 'critical' ? '#EF4444' :
                asset.risk_level === 'high' ? '#F97316' : '#1E3A8A',
        strokeWidth: Math.max(1, Math.min(1 + Math.floor((asset.event_count_24h || 0) / 3), 4)),
        strokeDasharray: asset.enforcement_state === 'enforcement' ? '4 2' : undefined,
        opacity: (asset.event_count_24h || 0) > 0 ? 0.85 : 0.35,
      },
    });
  });

  // Overlay edges from topology/edges (peer-to-peer comms)
  edges.forEach((e, idx) => {
    const srcId = `node-${e.source_ip}`;
    const dstId = `node-${e.dest_ip}`;
    const srcExists = nodes.find(n => n.id === srcId);
    const dstExists = nodes.find(n => n.id === dstId);
    if (!srcExists || !dstExists) return;
    const proto = (e.protocols || [])[0] || 'it';
    rfEdges.push({
      id: `peer-${idx}`,
      source: srcId,
      target: dstId,
      animated: e.status === 'suspicious',
      style: {
        stroke: PROTOCOL_COLORS[proto] || '#475569',
        strokeWidth: Math.min(1 + Math.floor(e.event_count / 10), 3),
        strokeDasharray: e.blocked ? '3 2' : undefined,
        opacity: 0.7,
      },
      label: proto.toUpperCase(),
      labelStyle: { fill: PROTOCOL_COLORS[proto] || '#475569', fontSize: 9 },
      labelBgStyle: { fill: 'rgba(255,255,255,0.9)' },
    });
  });

  return { nodes, rfEdges };
}

export default function TopologyAssets({ assets, edges, events, edgeNode, selectedNode, onSelectNode }) {
  const [filter, setFilter] = useState('All');

  const filteredAssets = useMemo(() => {
    // Use live assets; fall back to KNOWN_DEVICES for local Pi scenario
    const base = assets.length > 0 ? assets : Object.entries(KNOWN_DEVICES).map(([ip, kd]) => ({
      ip_address: ip, asset_name: knownDeviceName(ip, kd.name), asset_type: kd.role, vendor: kd.vendor,
      risk_level: null, event_count_24h: 0, enforcement_state: null, protocols_used: [], policy_hits: [],
    }));
    let list;
    switch (filter) {
      case 'Critical': list = base.filter(a => a.risk_level === 'critical'); break;
      case 'High':     list = base.filter(a => a.risk_level === 'high');     break;
      case 'Blocked':  list = base.filter(a => a.enforcement_state === 'enforcement'); break;
      default:         list = base; break;
    }
    // Sort: enforcement/blocked first, then critical, high, normal; within tier by event count desc
    return [...list].sort((a, b) => {
      if (a.enforcement_state === 'enforcement' && b.enforcement_state !== 'enforcement') return -1;
      if (b.enforcement_state === 'enforcement' && a.enforcement_state !== 'enforcement') return 1;
      const tier = r => r?.risk_level === 'critical' ? 0 : r?.risk_level === 'high' ? 1 : 2;
      if (tier(a) !== tier(b)) return tier(a) - tier(b);
      return (b.event_count_24h || 0) - (a.event_count_24h || 0);
    });
  }, [assets, filter]);

  const { nodes: rfNodes, rfEdges: rfEdgesData } = useMemo(
    () => buildAssetGraph(assets, edges, edgeNode, selectedNode?.id),
    [assets, edges, edgeNode, selectedNode]
  );

  // Sync nodes when data changes
  const syncedNodes = useMemo(() => {
    return rfNodes.map(n => ({
      ...n,
      data: { ...n.data, isSelected: selectedNode?.id === n.id },
    }));
  }, [rfNodes, selectedNode]);

  const onNodeClick = useCallback((_, node) => {
    if (node.id === 'cloud' || node.id === 'edge-agent') return;
    const ip = node.id.replace('node-', '');
    const assetData = assets.find(a => a.ip_address === ip) || {};
    const kd = KNOWN_DEVICES[ip] || {};
    onSelectNode({ id: node.id, ip, ...kd, ...assetData });
  }, [assets, onSelectNode]);

  const recentByIp = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      const ip = ev.dest_ip || ev.source_ip;
      if (!ip) return;
      if (!map[ip]) map[ip] = [];
      if (map[ip].length < 5) map[ip].push(ev);
    });
    return map;
  }, [events]);

  const summaryStats = useMemo(() => ({
    total: assets.length || Object.keys(KNOWN_DEVICES).length,
    critical: assets.filter(a => a.risk_level === 'critical').length,
    enforcement: assets.filter(a => a.enforcement_state === 'enforcement').length,
    edgeStatus: edgeNode?.status || 'Unknown',
  }), [assets, edgeNode]);

  return (
    <div className="flex h-full">
      {/* Left rail — asset list */}
      <div className="w-60 flex-shrink-0 flex flex-col border-r border-slate-200 bg-white/50">
        <div className="p-3 border-b border-slate-200">
          <div className="text-xs font-semibold text-slate-700 mb-2">Assets</div>
          <div className="flex flex-wrap gap-1">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${
                  filter === f
                    ? 'bg-cyan-100 text-cyan-700 border border-cyan-400'
                    : 'text-slate-600 border border-slate-200 hover:text-slate-700'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredAssets.length === 0 ? (
            <div className="p-4 text-xs text-slate-500 text-center">No assets match filter</div>
          ) : filteredAssets.map(asset => {
            const kd = KNOWN_DEVICES[asset.ip_address] || {};
            const name   = asset.asset_name || knownDeviceName(asset.ip_address, kd.name) || asset.ip_address;
            const vendor = asset.vendor || kd.vendor || '';
            const role   = deviceTypeLabel(name, vendor, asset.asset_type || kd.role || 'Device');
            const roleClass = deviceRoleClass(name, vendor, role);
            const nodeId = `node-${asset.ip_address}`;
            const isSelected = selectedNode?.id === nodeId;
            return (
              <button
                key={asset.ip_address}
                onClick={() => onSelectNode({ id: nodeId, ip: asset.ip_address, ...kd, ...asset })}
                className={`w-full text-left px-3 py-2 border-b border-slate-200 hover:bg-slate-100/50 transition-colors ${isSelected ? 'bg-slate-100/80' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold" style={{ color: ROLE_COLORS[roleClass] || '#475569' }}>
                    {role}
                  </span>
                  <div className="flex items-center gap-1">
                    {asset.event_count_24h > 0 && (
                      <span className="text-[10px] px-1 rounded bg-slate-200 text-slate-600">
                        {asset.event_count_24h}
                      </span>
                    )}
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: asset.risk_level === 'critical' ? '#EF4444' :
                                    asset.risk_level === 'high' ? '#F97316' : '#10B981'
                      }}
                    />
                  </div>
                </div>
                <div className="text-[12px] text-slate-700 leading-tight truncate">{name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{asset.ip_address}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Center — ReactFlow graph */}
      <div className="flex-1 relative etl-canvas" style={{ background: '#F8FAFC' }}>
        <ReactFlow
          nodes={syncedNodes}
          edges={rfEdgesData}
          nodeTypes={TOPOLOGY_NODE_TYPES}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.4}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Controls className="!bg-slate-100 !border-slate-200" />
          <Background color="#CBD5E1" variant={BackgroundVariant.Dots} gap={20} size={1} />
        </ReactFlow>
        <div className="absolute top-2 left-2 flex items-center gap-3 bg-white/80 border border-slate-200 rounded px-2 py-1">
          <span className="text-[10px] text-slate-600">LIVE ASSET GRAPH</span>
        </div>
      </div>

      {/* Right rail — detail */}
      <div className="w-72 flex-shrink-0 flex flex-col border-l border-slate-200 bg-white/50 overflow-y-auto">
        {selectedNode ? (
          <AssetDetail asset={selectedNode} recentEvents={recentByIp[selectedNode.ip] || []} />
        ) : (
          <SummaryStats stats={summaryStats} edgeNode={edgeNode} />
        )}
      </div>
    </div>
  );
}

function AssetDetail({ asset, recentEvents }) {
  // asset.name is the KNOWN_DEVICES entry's name (spread in by the click handlers); it goes through
  // knownDeviceName so the .75/.76 entries read as the devices behind them, as on the list and graph.
  const knownName = knownDeviceName(asset.ip, asset.name);
  const detailName = asset.asset_name || knownName;
  const role = deviceTypeLabel(detailName, asset.vendor, asset.role || asset.asset_type);
  const roleColor = ROLE_COLORS[deviceRoleClass(detailName, asset.vendor, role)] || '#475569';
  const riskColor = asset.risk_level === 'critical' ? '#EF4444' :
                    asset.risk_level === 'high' ? '#F97316' : '#10B981';
  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${roleColor}22`, color: roleColor }}>
            {role}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded border" style={{ color: riskColor, borderColor: `${riskColor}44`, background: `${riskColor}11` }}>
            {(asset.risk_level || 'normal').toUpperCase()}
          </span>
        </div>
        <div className="text-sm font-semibold text-slate-900">{knownName || asset.asset_name}</div>
        <div className="text-[11px] text-slate-600 font-mono">{asset.ip}</div>
      </div>

      <div className="space-y-1.5">
        {[
          ['Vendor', asset.vendor],
          ['MAC', asset.mac_address],
          ['Criticality', asset.criticality_level],
        ].map(([label, val]) => val && (
          <div key={label} className="flex justify-between text-xs">
            <span className="text-slate-600">{label}</span>
            <span className="text-slate-700 font-mono text-[11px]">{val}</span>
          </div>
        ))}
      </div>

      {(asset.protocols_used || []).length > 0 && (
        <div>
          <div className="text-[11px] text-slate-600 mb-1.5">Protocols</div>
          <div className="flex flex-wrap gap-1">
            {asset.protocols_used.map(p => (
              <span key={p} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${PROTOCOL_COLORS[p?.toLowerCase()] || '#475569'}22`, color: PROTOCOL_COLORS[p?.toLowerCase()] || '#475569', border: `1px solid ${PROTOCOL_COLORS[p?.toLowerCase()] || '#475569'}44` }}>
                {p?.toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      )}

      {(asset.policy_hits || []).length > 0 && (
        <div>
          <div className="text-[11px] text-slate-600 mb-1.5">Policy Hits (24h)</div>
          <div className="space-y-0.5">
            {asset.policy_hits.map(p => (
              <div key={p} className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-300 truncate">{p}</div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[11px] text-slate-600 mb-1.5">Recent Events</div>
        {recentEvents.length === 0 ? (
          <div className="text-[11px] text-slate-500">No recent events</div>
        ) : recentEvents.map(ev => (
          <div key={ev.id} className="mb-1.5 p-2 rounded bg-slate-100/60 border border-slate-200">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[10px] font-semibold" style={{ color: SEVERITY_COLORS[ev.severity] || '#475569' }}>
                {ev.severity}
              </span>
              <span className="text-[10px] text-slate-500">{ev.protocol?.toUpperCase()}</span>
            </div>
            <div className="text-[11px] text-slate-600 leading-tight line-clamp-2">{ev.summary}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryStats({ stats, edgeNode }) {
  return (
    <div className="p-4 space-y-3">
      <div className="text-xs font-semibold text-slate-600 mb-2">System Summary</div>
      {[
        { label: 'Registered Assets', value: stats.total, color: '#22D3EE' },
        { label: 'Critical Risk', value: stats.critical, color: '#EF4444' },
        { label: 'Enforcement Events', value: stats.enforcement, color: '#F97316' },
      ].map(s => (
        <div key={s.label} className="flex justify-between items-center">
          <span className="text-[12px] text-slate-600">{s.label}</span>
          <span className="text-sm font-bold" style={{ color: s.color }}>{s.value}</span>
        </div>
      ))}
      <div className="pt-2 border-t border-slate-200">
        <div className="flex justify-between items-center">
          <span className="text-[12px] text-slate-600">Celerway Edge Agent</span>
          <span className={`text-[11px] font-semibold ${edgeNode?.status === 'Online' || edgeNode?.status === 'Active' ? 'text-emerald-700' : 'text-red-700'}`}>
            {edgeNode?.status || 'Offline'}
          </span>
        </div>
        {edgeNode?.edge_node_id && (
          <div className="text-[10px] text-slate-500 mt-0.5">{edgeNodeDisplayName(edgeNode)}</div>
        )}
      </div>
      <div className="pt-2 text-[11px] text-slate-500 leading-relaxed">
        Click any node in the graph or row in the asset list to inspect details.
      </div>
    </div>
  );
}
